<template>
    <v-autocomplete v-bind="attrs"
                    :custom-filter="matchesCompany"
                    :items="items"
                    :model-value="modelValue"
                    item-title="company_name"
                    item-value="id"
                    @update:model-value="value => emit('update:modelValue', value)">
        <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :subtitle="identifiers(item.raw)">
                <!-- Vuetify draws the multi-select checkbox inside its own #item
                     template, not in itemProps, so overriding the slot removes it.
                     Put it back or a multiple picker shows no checkboxes at all. -->
                <template v-if="isMultiple" #prepend="{ isSelected }">
                    <v-checkbox-btn :model-value="isSelected" tabindex="-1"/>
                </template>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>

<script setup>
/**
 * Company picker for super admins.
 *
 * A Go account's company_name is just the owner's personal name, so the name on
 * its own does not identify a company: in production one name is the exact
 * company_name of six different companies, and two rows share both a name and an
 * email address and differ only by account code and id. The row therefore
 * carries the owner name (when it adds anything the title does not already say),
 * the email, the account code and the id, and typing searches all of them.
 */
import {computed, useAttrs} from "vue";

defineOptions({inheritAttrs: false});

defineProps({
    modelValue: {type: [Number, String, Array, Object], default: null},
    // Rows from GET companies/list: id, company_name, email, account_code, owner_name.
    items: {type: Array, default: () => []},
});
const emit = defineEmits(['update:modelValue']);
const attrs = useAttrs();

// Everything the call site sets - rules, loading, clearable, multiple, density,
// variant, class - falls through to the field. Vue keeps declared emits out of
// $attrs, so the parent's own @update:model-value is not in here and cannot be
// bound to the inner field a second time; emit() reaches it exactly once, after
// v-model has written back.

// A valueless `multiple` attribute arrives as an empty string, which is falsy, so
// the obvious truthiness test would miss half the call sites.
const isMultiple = computed(() => attrs.multiple !== undefined && attrs.multiple !== false);

const owner = (row) => {
    const name = String(row?.owner_name ?? '').trim();
    if (!name) return '';
    // Suppress it when the title already says it, which is every Go account.
    return name.toLowerCase() === String(row?.company_name ?? '').trim().toLowerCase() ? '' : name;
};

const identifiers = (row) => [
    owner(row),
    row?.email,
    row?.account_code ? `#${row.account_code}` : '',
    row?.id ? `ID ${row.id}` : '',
].filter(Boolean).join('  ·  ');

const matchesCompany = (_value, query, item) => {
    const needle = String(query ?? '').trim().toLowerCase();
    if (!needle) return true;
    const row = item?.raw ?? {};
    return ['company_name', 'owner_name', 'email', 'account_code', 'id']
        .some((key) => String(row[key] ?? '').toLowerCase().includes(needle));
};
</script>
